import React, { useEffect, useState } from 'react';
import { Box, Grid, Avatar, Typography, Button, Card, CardContent, Divider, CircularProgress, Alert } from '@mui/material';
import MainCard from 'components/MainCard';
import avatar1 from 'assets/images/users/avatar-1.png';
import { fetchOwnerProfile } from 'api/owner';
import { fetchOwnerSubstores } from 'api/substore';

export default function Profile() {
  const [owner, setOwner] = useState(null);
  const [substores, setSubstores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Fetch owner profile first, then substores, with a small delay in between
    const fetchSequentially = async () => {
      try {
        // Use cached substores if available
        let cachedSubstores = null;
        try {
          cachedSubstores = JSON.parse(localStorage.getItem('substores'));
        } catch (e) {}

        const ownerData = await fetchOwnerProfile();
        setOwner(ownerData);
        await new Promise((resolve) => setTimeout(resolve, 100));

        let substoresData;
        if (cachedSubstores && Array.isArray(cachedSubstores)) {
          substoresData = cachedSubstores;
        } else {
          substoresData = await fetchOwnerSubstores();
          // If response is an object with substores key, parse it
          if (substoresData && typeof substoresData === 'object' && substoresData.substores) {
            substoresData = substoresData.substores;
          }
          // Cache substores for future use
          localStorage.setItem('substores', JSON.stringify(substoresData));
        }
        setSubstores(Array.isArray(substoresData) ? substoresData : []);
        setLoading(false);
      } catch (err) {
        setError('Failed to load profile or substores');
        setLoading(false);
      }
    };
    fetchSequentially();
  }, []);

  if (loading) {
    return (
      <Box sx={{ width: '100%', minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ width: '100%', minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', minHeight: '80vh', p: 3 }}>
      {/* User Profile Card */}
      <MainCard
        title="User Profile"
        sx={{ mb: 4 }}
        secondary={
          <Button variant="contained" color="primary">
            Edit Profile
          </Button>
        }
      >
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Avatar src={avatar1} alt={owner.username} sx={{ width: 160, height: 160 }} />
          </Grid>
          <Grid item xs={12} sm={8}>
            <Box>
              <Typography variant="h5" color="text.secondary" fontWeight={600} gutterBottom>
                Username:
              </Typography>
              <Typography variant="h3" fontWeight={700} mb={2}>
                {owner.username}
              </Typography>
              <Typography variant="h5" color="text.secondary" fontWeight={600} gutterBottom>
                Email:
              </Typography>
              <Typography variant="h4" fontWeight={600} mb={2}>
                {owner.email}
              </Typography>
              <Typography variant="h5" color="text.secondary" fontWeight={600} gutterBottom>
                Phone Number:
              </Typography>
              <Typography variant="h4" fontWeight={600}>
                {owner.phone}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </MainCard>

      {/* Main Store Card */}
      <MainCard title="Main Store" sx={{ mb: 4 }}>
        <Typography variant="h5" color="text.secondary" fontWeight={600} gutterBottom>
          Store Name:
        </Typography>
        <Typography variant="h4" fontWeight={600} mb={2}>
          {owner.store_name || 'N/A'}
        </Typography>
        <Typography variant="h5" color="text.secondary" fontWeight={600} gutterBottom>
          Store Address:
        </Typography>
        <Typography variant="h4" fontWeight={600}>
          {owner.store_address || 'N/A'}
        </Typography>
      </MainCard>

      {/* Sub-Stores Card */}
      <MainCard
        title="Sub-Stores"
        secondary={
          <Typography variant="subtitle1" color="primary">
            Total: {substores.length} stores
          </Typography>
        }
      >
        <Grid container spacing={3}>
          {Array.isArray(substores) ? substores.map((store, index) => (
            <Grid item xs={12} md={6} key={store.id}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h4" fontWeight={600} gutterBottom>
                    {store.name}
                  </Typography>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="body1">{store.address}</Typography>
                  {/* <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Join Code: {store.join_code}
                  </Typography> */}
                </CardContent>
              </Card>
            </Grid>
          )) : null}
        </Grid>
      </MainCard>
    </Box>
  );
}
